import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { notStowedVolume, notStowedWeightInTanks } from './stowageOperation/stowageOperationUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { getTankContentInBerthCall, updateManifest } from './manifest/cargoManifestUtils';
import { fillTanks } from './stowageOperation/fillTankUtils';
import { updateUnstowOperationsForCargo } from './stowageOperation/autoUnstowUtils';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import Decimal from 'decimal.js';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';

export const autoStow = (vessel: Vessel, stowagePlan: StowagePlan, portCall: RotationPortCall, berthCall: RotationBerthCall) => {
    let stowagePlanChanges: StowagePlanChange[] = [];
    if (berthCall) {
        stowagePlanChanges = stowagePlanChanges.concat(autoStowInBerthCall(vessel, stowagePlan, berthCall));
    } else if (portCall) {
        // TODO: PERFORMANCE - Potential side effect: forEach modifies stowagePlanChanges using concatenation assignment, which could be replaced with more functional approach like flatMap for better readability and immutability.
        portCall.berthCalls.forEach((bc) => (stowagePlanChanges = stowagePlanChanges.concat(autoStowInBerthCall(vessel, stowagePlan, bc))));
    } else {
        // TODO: PERFORMANCE - Same potential side effect issue as above line - forEach with mutable assignment could be replaced with flatMap approach.
        stowagePlan.berthCalls.forEach((bc) => (stowagePlanChanges = stowagePlanChanges.concat(autoStowInBerthCall(vessel, stowagePlan, bc))));
    }

    return stowagePlanChanges;
};

const autoStowInBerthCall = (vessel: Vessel, stowagePlan: StowagePlan, berthCall: RotationBerthCall) => {
    let stowagePlanChanges: StowagePlanChange[] = [];

    const cargoOperationAndWeights = stowagePlan.cargoOperations
        .filter((co) => co.berthCall.berthCallGuid === berthCall.berthCallGuid)
        .map((co) => ({ operation: co, notStowedWeight: notStowedWeightInTanks(stowagePlan, co) }))
        .filter((o) => o.notStowedWeight.gt(0))
        .sort((a, b) => a.notStowedWeight.minus(b.notStowedWeight).toNumber());

    cargoOperationAndWeights.forEach((o) => {
        const cargoTanks = vessel.cargoTanks.sort((a, b) => a.volumeAt98Percent - b.volumeAt98Percent);
        cargoTanks
            .filter(
                (tank) =>
                    o.notStowedWeight.toNumber() <= tank.maxWeightAt98Percent &&
                    notStowedVolume(stowagePlan, o.operation).toNumber() <= tank.volumeAt98Percent
            )
            .forEach((tank) => {
                stowagePlanChanges = stowagePlanChanges.concat(tryStowOperationInTank(vessel, stowagePlan, berthCall, o.operation, tank));
            });

        cargoTanks.forEach((tank) => {
            stowagePlanChanges = stowagePlanChanges.concat(tryStowOperationInTank(vessel, stowagePlan, berthCall, o.operation, tank));
        });
    });

    return stowagePlanChanges;
};

const tryStowOperationInTank = (
    vessel: Vessel,
    stowagePlan: StowagePlan,
    berthCall: RotationBerthCall,
    cargoOperation: RotationCargoOperation,
    tank: Tank
) => {
    const tankContent = getTankContentInBerthCall(stowagePlan, berthCall, tank);
    const notStowedWeight = notStowedWeightInTanks(stowagePlan, cargoOperation);
    if (!tankContent?.cargoContents?.length && notStowedWeight.gt(0)) {
        return stowOperationInTank(vessel, stowagePlan, cargoOperation, tank, tankContent, notStowedWeight);
    }

    return [];
};

const stowOperationInTank = (
    vessel: Vessel,
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    tank: Tank,
    tankContent: TankContent,
    notStowedWeight: Decimal
) => {
    const fillTanksResponse = fillTanks(
        stowagePlan,
        [{ tank: tank, cargoContent: tankContent?.cargoContents?.[0] }],
        cargoOperation,
        notStowedWeight
    );
    const unstowStowagePlanChanges = updateUnstowOperationsForCargo(stowagePlan, cargoOperation);
    updateManifest(stowagePlan, vessel);

    return fillTanksResponse.stowagePlanChanges.concat(unstowStowagePlanChanges);
};
